/**
 * Employer Cost Transparency
 *
 * Per-employee and program cost visibility:
 * - Program cost breakdown
 * - Per-employee cost view
 * - ROI calculator
 * - Budget forecasting
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Users,
  PieChart,
  BarChart3,
  Calculator,
  Target,
  Gift,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  Minus,
  Filter,
  Download,
  RefreshCw,
  Loader2,
  ChevronRight,
  Calendar,
  Clock,
  Wallet,
  CreditCard,
  Receipt,
  AlertCircle,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/contexts/AuthContext';
import { getWeaveinApiUrl } from '@/src/config';
import { authFetch } from '@/lib/authFetch';
import { useTabbedChrome } from '@/components/weavein';

interface ProgramCost {
  id: number;
  name: string;
  category: string;
  enrolledCount: number;
  costPerEmployee: number;
  totalCost: number;
  utilizationRate: number;
  roi: number;
  trend: 'up' | 'down' | 'stable';
  trendPercent: number;
}

interface CostSummary {
  totalAnnualCost: number;
  costPerEmployee: number;
  totalEmployees: number;
  enrolledEmployees: number;
  averageROI: number;
  budgetUsed: number;
  budgetTotal: number;
  projectedAnnual: number;
  vsLastYear: number;
}

interface MonthlySpend {
  month: string;
  actual: number;
  projected: number;
  budget: number;
}

interface EmployerCostTransparencyProps {
  employerId: number;
  onNavigate?: (tab: string) => void;
}

export default function EmployerCostTransparency({
  employerId,
  onNavigate,
}: EmployerCostTransparencyProps) {
  const tabbed = useTabbedChrome();
  const { t } = useAppTranslation();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState<'monthly' | 'quarterly' | 'annual'>('annual');
  const [programCosts, setProgramCosts] = useState<ProgramCost[]>([]);
  const [summary, setSummary] = useState<CostSummary>({
    totalAnnualCost: 0,
    costPerEmployee: 0,
    totalEmployees: 0,
    enrolledEmployees: 0,
    averageROI: 0,
    budgetUsed: 0,
    budgetTotal: 0,
    projectedAnnual: 0,
    vsLastYear: 0,
  });
  const [monthlySpend, setMonthlySpend] = useState<MonthlySpend[]>([]);

  // Fetch cost data
  useEffect(() => {
    const fetchCostData = async () => {
      setLoading(true);
      try {
        // Fetch employer data
        const employerUrl = getWeaveinApiUrl(`/api/employers/${employerId}`);
        const employerResponse = await authFetch(employerUrl);

        let totalEmployees = 100;
        if (employerResponse.ok) {
          const data = await employerResponse.json();
          totalEmployees = data.employee_count || 100;
        }

        // No mock data — cost data comes from actual billing API
        // TODO: Integrate with /api/employer/{id}/program-costs endpoint
        setProgramCosts([]);
        setSummary({
          totalAnnualCost: 0,
          costPerEmployee: 0,
          totalEmployees,
          enrolledEmployees: 0,
          averageROI: 0,
          budgetUsed: 0,
          budgetTotal: 0,
          projectedAnnual: 0,
          vsLastYear: 0,
        });
        setMonthlySpend([]);

      } catch (error) {
        console.error('Error fetching cost data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (employerId) {
      fetchCostData();
    }
  }, [employerId, token]);

  // Category breakdown
  const categoryBreakdown = useMemo(() => {
    const categories: Record<string, { total: number; count: number }> = {};
    programCosts.forEach(p => {
      if (!categories[p.category]) {
        categories[p.category] = { total: 0, count: 0 };
      }
      categories[p.category].total += p.totalCost;
      categories[p.category].count += p.enrolledCount;
    });

    return Object.entries(categories).map(([category, data]) => ({
      category,
      total: data.total,
      count: data.count,
      percent: Math.round((data.total / summary.totalAnnualCost) * 100),
    })).sort((a, b) => b.total - a.total);
  }, [programCosts, summary.totalAnnualCost]);

  const getCategoryColor = (category: string): string => {
    const colors: Record<string, string> = {
      Wellness: 'bg-green-500',
      Health: 'bg-blue-500',
      Financial: 'bg-amber-500',
      Family: 'bg-pink-500',
      Career: 'bg-purple-500',
      Commuter: 'bg-cyan-500',
    };
    return colors[category] || 'bg-gray-500';
  };

  const getTrendIcon = (trend: string, percent: number) => {
    if (trend === 'up') return <ArrowUp className={`w-4 h-4 ${percent > 0 ? 'text-green-500' : 'text-red-500'}`} />;
    if (trend === 'down') return <ArrowDown className={`w-4 h-4 ${percent < 0 ? 'text-green-500' : 'text-red-500'}`} />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  const handleNavigate = (tab: string) => {
    if (onNavigate) {
      onNavigate(tab);
    } else {
      const event = new CustomEvent('changeTab', { detail: tab });
      window.dispatchEvent(event);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-gray-600">{t('employer.cost.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-full overflow-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          {!tabbed && (
          <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl">
            <DollarSign className="h-6 w-6 text-white" />
          </div>
          )}
          <div>
            {!tabbed && (
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {t('employer.cost.title')}
            </h1>
            )}
            <p className="text-gray-600 dark:text-gray-400">
              {t('employer.cost.subtitle')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={timeframe}
            onChange={(e) => setTimeframe(e.target.value as 'monthly' | 'quarterly' | 'annual')}
            className="px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 text-sm"
          >
            <option value="monthly">{t('employer.cost.monthly')}</option>
            <option value="quarterly">{t('employer.cost.quarterly')}</option>
            <option value="annual">{t('employer.cost.annual')}</option>
          </select>
          <Button variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" />
            {t('employer.common.export')}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <Wallet className="h-5 w-5 text-green-500" />
              <span className="text-sm text-muted-foreground">{t('employer.cost.totalAnnualCost')}</span>
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              ${(summary.totalAnnualCost / 1000).toFixed(0)}K
            </div>
            <div className="flex items-center gap-1 mt-1 text-sm">
              <ArrowUp className="w-3 h-3 text-amber-500" />
              <span className="text-amber-600">{t('employer.cost.vsLastYear', { percent: summary.vsLastYear })}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <Users className="h-5 w-5 text-blue-500" />
              <span className="text-sm text-muted-foreground">{t('employer.cost.costPerEmployee')}</span>
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              ${summary.costPerEmployee.toLocaleString()}
            </div>
            <div className="text-sm text-gray-500 mt-1">
              {t('employer.cost.enrolledOfTotal', { enrolled: summary.enrolledEmployees, total: summary.totalEmployees })}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-5 w-5 text-purple-500" />
              <span className="text-sm text-muted-foreground">{t('employer.cost.averageRoi')}</span>
            </div>
            <div className="text-2xl font-bold text-green-600">
              {summary.averageROI}%
            </div>
            <div className="text-sm text-gray-500 mt-1">
              {t('employer.cost.returnOnInvestment')}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <Target className="h-5 w-5 text-amber-500" />
              <span className="text-sm text-muted-foreground">{t('employer.cost.budgetUsed')}</span>
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {summary.budgetUsed}%
            </div>
            <Progress value={summary.budgetUsed} className="h-2 mt-2" />
          </CardContent>
        </Card>
      </div>

      {/* Two Column Layout */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Category Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="w-5 h-5 text-purple-600" />
              {t('employer.cost.spendingByCategory')}
            </CardTitle>
            <CardDescription>
              {t('employer.cost.spendingByCategoryDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {categoryBreakdown.map((cat) => (
                <div key={cat.category}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${getCategoryColor(cat.category)}`} />
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {cat.category}
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="font-semibold text-gray-900 dark:text-gray-100">
                        ${(cat.total / 1000).toFixed(0)}K
                      </span>
                      <span className="text-gray-500 text-sm ml-2">
                        ({cat.percent}%)
                      </span>
                    </div>
                  </div>
                  <Progress
                    value={cat.percent}
                    className={`h-2 ${getCategoryColor(cat.category).replace('bg-', '[&>div]:bg-')}`}
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Monthly Trend */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-600" />
              {t('employer.cost.monthlySpendingTrend')}
            </CardTitle>
            <CardDescription>
              {t('employer.cost.monthlySpendingTrendDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {monthlySpend.slice(0, 6).map((month) => {
                const maxValue = Math.max(month.actual, month.projected, month.budget);
                return (
                  <div key={month.month} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-gray-700 dark:text-gray-300 w-10">
                        {month.month}
                      </span>
                      <div className="flex gap-4 text-xs">
                        {month.actual > 0 && (
                          <span className="text-green-600">
                            ${(month.actual / 1000).toFixed(0)}K actual
                          </span>
                        )}
                        {month.projected > 0 && (
                          <span className="text-blue-600">
                            ${(month.projected / 1000).toFixed(0)}K projected
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1 h-4">
                      {month.actual > 0 && (
                        <div
                          className="bg-green-500 rounded-sm"
                          style={{ width: `${(month.actual / maxValue) * 100}%` }}
                        />
                      )}
                      {month.projected > 0 && (
                        <div
                          className="bg-blue-300 rounded-sm border-2 border-dashed border-blue-500"
                          style={{ width: `${(month.projected / maxValue) * 100}%` }}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-center gap-6 mt-4 text-xs text-gray-500">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-500 rounded-sm" />
                <span>{t('employer.cost.actual')}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-300 border-2 border-dashed border-blue-500 rounded-sm" />
                <span>{t('employer.cost.projected')}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Program Cost Details */}
      <Card className="overflow-hidden">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="w-5 h-5 text-pink-600" />
            {t('employer.cost.programCostBreakdown')}
          </CardTitle>
          <CardDescription>
            {t('employer.cost.programCostBreakdownDesc')}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0 sm:p-6">
          <div className="overflow-x-auto max-w-full">
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-400">
                    {t('employer.cost.program')}
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-600 dark:text-gray-400">
                    {t('employer.cost.enrolled')}
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-600 dark:text-gray-400">
                    {t('employer.cost.costPerEmployeeHeader')}
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-600 dark:text-gray-400">
                    {t('employer.cost.totalCost')}
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-600 dark:text-gray-400">
                    {t('employer.cost.utilization')}
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-600 dark:text-gray-400">
                    {t('employer.cost.roi')}
                  </th>
                  <th className="text-center py-3 px-4 font-medium text-gray-600 dark:text-gray-400">
                    {t('employer.cost.trend')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {programCosts.map((program) => (
                  <tr
                    key={program.id}
                    className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                  >
                    <td className="py-4 px-4">
                      <div>
                        <div className="font-medium text-gray-900 dark:text-gray-100">
                          {program.name}
                        </div>
                        <Badge variant="secondary" className="text-xs mt-1">
                          {program.category}
                        </Badge>
                      </div>
                    </td>
                    <td className="text-right py-4 px-4 text-gray-600 dark:text-gray-400">
                      {program.enrolledCount}
                    </td>
                    <td className="text-right py-4 px-4 text-gray-900 dark:text-gray-100">
                      ${program.costPerEmployee}
                    </td>
                    <td className="text-right py-4 px-4 font-semibold text-gray-900 dark:text-gray-100">
                      ${(program.totalCost / 1000).toFixed(1)}K
                    </td>
                    <td className="text-right py-4 px-4">
                      <div className="flex items-center justify-end gap-2">
                        <span className={program.utilizationRate >= 70 ? 'text-green-600' : program.utilizationRate >= 50 ? 'text-amber-600' : 'text-red-600'}>
                          {program.utilizationRate}%
                        </span>
                      </div>
                    </td>
                    <td className="text-right py-4 px-4">
                      <span className={`font-semibold ${program.roi >= 300 ? 'text-green-600' : program.roi >= 150 ? 'text-blue-600' : 'text-amber-600'}`}>
                        {program.roi}%
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center justify-center gap-1">
                        {getTrendIcon(program.trend, program.trendPercent)}
                        <span className={`text-sm ${program.trendPercent > 0 ? 'text-green-600' : program.trendPercent < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                          {program.trendPercent > 0 ? '+' : ''}{program.trendPercent}%
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 dark:bg-gray-800">
                  <td className="py-4 px-4 font-semibold text-gray-900 dark:text-gray-100">
                    {t('employer.cost.total')}
                  </td>
                  <td className="text-right py-4 px-4 font-semibold text-gray-900 dark:text-gray-100">
                    {summary.enrolledEmployees}
                  </td>
                  <td className="text-right py-4 px-4 font-semibold text-gray-900 dark:text-gray-100">
                    ${summary.costPerEmployee}
                  </td>
                  <td className="text-right py-4 px-4 font-bold text-gray-900 dark:text-gray-100">
                    ${(summary.totalAnnualCost / 1000).toFixed(0)}K
                  </td>
                  <td className="text-right py-4 px-4 font-semibold text-gray-900 dark:text-gray-100">
                    -
                  </td>
                  <td className="text-right py-4 px-4 font-bold text-green-600">
                    {summary.averageROI}%
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Budget Forecast */}
      <Card className="border-purple-200 dark:border-purple-800 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-purple-600" />
            {t('employer.cost.budgetForecast')}
          </CardTitle>
          <CardDescription>
            {t('employer.cost.budgetForecastDesc')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-sm text-gray-500 mb-1">{t('employer.cost.currentYearBudget')}</div>
              <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                ${(summary.budgetTotal / 1000).toFixed(0)}K
              </div>
              <div className="text-sm text-gray-500 mt-1">
                {t('employer.cost.percentUtilized', { percent: summary.budgetUsed })}
              </div>
            </div>
            <div className="text-center">
              <div className="text-sm text-gray-500 mb-1">{t('employer.cost.projectedAnnual')}</div>
              <div className="text-3xl font-bold text-blue-600">
                ${(summary.projectedAnnual / 1000).toFixed(0)}K
              </div>
              <div className="text-sm text-amber-600 mt-1 flex items-center justify-center gap-1">
                <ArrowUp className="w-3 h-3" />
                {t('employer.cost.growthExpected', { percent: 8 })}
              </div>
            </div>
            <div className="text-center">
              <div className="text-sm text-gray-500 mb-1">{t('employer.cost.netValueCreated')}</div>
              <div className="text-3xl font-bold text-green-600">
                ${((summary.totalAnnualCost * (summary.averageROI / 100)) / 1000).toFixed(0)}K
              </div>
              <div className="text-sm text-gray-500 mt-1">
                {t('employer.cost.basedOnRoi', { percent: summary.averageROI })}
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-white/60 dark:bg-gray-800/60 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-500 mt-0.5" />
              <div>
                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-1">
                  {t('employer.cost.optimizationOpportunity')}
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('employer.cost.optimizationOpportunityDesc', {
                    additionalValue: ((summary.totalAnnualCost * 0.12 * (summary.averageROI / 100)) / 1000).toFixed(0),
                    additionalInvestment: ((summary.totalAnnualCost * 0.12) / 1000).toFixed(0)
                  })}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
